import {AppUrls} from 'shared/config/model/urls';

import React, {useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import cn from 'classnames';
import {SubmitHandler, useForm} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';
import {InvisibleSmartCaptcha} from '@yandex/smart-captcha';
import {t} from '@lingui/core/macro';

import EmailInput from 'shared/ui/Input/InputEmail';
import Button from 'shared/ui/Button/Button/Button';
import Checkbox from 'shared/ui/Checkbox/Checkbox';
import InputPassword from 'shared/ui/Input/InputPassword';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import InputName from 'shared/ui/Input/InputName';
import {useOAuthPopup} from 'features/auth/model/useOAuthPopup';
import {
    IRegistrationForm,
    fetchMe,
    registerUser,
    registerUserLoadingSelector,
    registerUserSuccessSelector,
} from 'entities/user';
import {TypedDispatch} from 'shared/model/types';
import {redirectToDashboard} from 'shared/api/redirects';
import {getClientDefaultTimeZone} from 'shared/lib/datetime';
import {reachGoal, YM_GOALS} from 'shared/lib/analytics';
import {generateFirstName, generateLastName} from 'features/auth/lib/faker';
import {isTranslate} from 'shared/lib/lang';

import AuthWrapper from './AuthWrapper';
import RewardBlock from './RewardBlock/RewardBlock';

import OAuthButtons from './OAuthButtons/OAuthButtons';

import styles from './Auth.module.scss';

const Registration: React.FC = () => {
    const dispatch = useDispatch<TypedDispatch>();
    const navigate = useNavigate();
    const {register, handleSubmit, formState, control, setValue} = useForm<IRegistrationForm>({mode: 'onChange'});
    const {errors, isValid} = formState;
    const registerUserSuccess = useSelector(registerUserSuccessSelector);
    const isLoading = useSelector(registerUserLoadingSelector);
    const [fieldsCleared, setFieldsCleared] = useState({
        first_name: false,
        last_name: false,
    });
    const [token, setToken] = useState('');
    const [visible, setVisible] = useState(false);
    const isCaptchaEnabled = process.env.REACT_APP_CAPTCHA_ENABLED !== 'false';
    const { initOAuth, isLoading: oauthLoading, error: oauthError } = useOAuthPopup();

    useEffect(() => {
        if (isCaptchaEnabled) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setVisible(true);

            const interval = setInterval(() => {
                setVisible(false);
                setTimeout(() => setVisible(true), 100);
            }, 120000);

            return () => clearInterval(interval);
        }
    }, [isCaptchaEnabled]);

    useEffect(() => {
        setValue('first_name', generateFirstName());
        setValue('last_name', generateLastName());
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setFieldsCleared({ first_name: false, last_name: false });
    }, [setValue]);
    useEffect(() => {
        if (registerUserSuccess) {
            dispatch(fetchMe())
                .then(() => {
                    redirectToDashboard(navigate);
                });
        }
    }, [registerUserSuccess, dispatch, navigate]);

    const handleFieldClick = (fieldName: 'first_name' | 'last_name') => {
        if (!fieldsCleared[fieldName]) {
            setValue(fieldName, '');
            setFieldsCleared(prev => ({ ...prev, [fieldName]: true }));
        }
    };

    const onSubmit: SubmitHandler<IRegistrationForm> = (formData) => {
        dispatch(registerUser({
            ...formData,
            timezone: getClientDefaultTimeZone(),
            agree_to_newsletter: formData.agree_to_newsletter,
            captcha_token: isCaptchaEnabled ? token : '',
        })).then((result) => {
            if (registerUser.fulfilled.match(result)) {
                reachGoal(YM_GOALS.SUBMIT_FORM_REGISTRATION)
            }
        });
    };

    return (
        <AuthWrapper>
            <div className={styles.title_container}>
                <h2 className={styles.title}>
                    {t`Создайте свой аккаунт`}
                </h2>
                {!isTranslate() && <RewardBlock/>}
            </div>
            {
                !isTranslate() &&
                    <OAuthButtons onSelect={initOAuth} isLoading={oauthLoading}/>
            }
            {oauthError && <div className={styles.oauthError}>{oauthError}</div>}
            {
                !isTranslate() && <div className={styles.emailTitleRegistration}>Регистрация по почте</div>
            }
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="register-form"
            >
                <FormGroupWithError error={errors?.first_name?.message}>
                    <InputName
                        name="first_name"
                        placeholder={t`Имя`}
                        register={register}
                        control={control}
                        required
                        placeholderToLabel
                        onClick={() => handleFieldClick('first_name')}
                    />
                </FormGroupWithError>
                <FormGroupWithError error={errors?.last_name?.message}>
                    <InputName
                        name="last_name"
                        placeholder={t`Фамилия`}
                        register={register}
                        control={control}
                        required
                        placeholderToLabel
                        onClick={() => handleFieldClick('last_name')}
                    />
                </FormGroupWithError>
                <FormGroupWithError error={errors?.email?.message}>
                    <EmailInput
                        register={register}
                        control={control}
                        required
                        placeholderToLabel
                    />
                </FormGroupWithError>
                <FormGroupWithError error={errors?.password?.message}>
                    <InputPassword
                        register={register}
                        control={control}
                        placeholderToLabel
                    />
                </FormGroupWithError>
                {
                    !isTranslate() && <FormGroupWithError
                        error={errors?.license_agreement?.message}
                    >
                        <Checkbox
                            id="agree_to_newsletter"
                            {...register('agree_to_newsletter')}
                        >
                            <span
                                className={styles.agree}>
                                {t`Я даю`}&nbsp;
                                <Link
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    to={AppUrls.SUBSCRIPTION}
                                    className={styles.agree_link}
                                >
                                    {t`согласие`}
                                </Link>
                            &nbsp;{t`на получение важных уведомлений, специальных предложений`}
                                <br/> {t`и новостей от Overseer`}
                            </span>
                        </Checkbox>
                    </FormGroupWithError>
                }

                <FormGroupWithError
                    error={errors?.privacy_policy?.message}
                >
                    <Checkbox
                        id="privacy_policy"
                        error={!!errors?.privacy_policy}
                        {...register(
                            'privacy_policy', {
                                required: {
                                    value: true,
                                    message: t`Обязательное поле`,
                                },
                            },
                        )}
                    >
                        <span className={styles.agree}>
                            {t`Создавая аккаунт, я принимаю условия`}<br/>
                            <Link
                                target="_blank"
                                rel="noopener noreferrer"
                                to={AppUrls.LICENSE}
                                className={styles.agree_link}
                            >
                                {t`лицензионного договора`}
                            </Link>
                            &nbsp;{t`и даю`}&nbsp;
                            {
                                !isTranslate() && (
                                    <>
                                        <Link
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            to={AppUrls.CONSENT}
                                            className={styles.agree_link}
                                        >
                                            {t`согласие`}&nbsp;
                                        </Link>
                                        {t`на обработку моих персональных данных согласно соответствующей`}&nbsp;
                                    </>
                                )
                            }
                            <Link
                                target="_blank"
                                rel="noopener noreferrer"
                                to={AppUrls.PRIVACY}
                                className={styles.agree_link}
                            >
                                {t`политике`}
                            </Link>
                        </span>
                    </Checkbox>
                </FormGroupWithError>

                {isCaptchaEnabled && (
                    <InvisibleSmartCaptcha
                        sitekey={process.env.REACT_APP_CAPTCHA_KEY ?? ''}
                        onSuccess={setToken}
                        hideShield
                        visible={visible}
                    />
                )}
                <div className="form-group">
                    <Button
                        type="submit"
                        loading={isLoading}
                        className={styles.button}
                        disabled={!isValid}
                        block
                    >
                        {t`Создать аккаунт`}
                    </Button>
                </div>
            </form>

            <p className={cn(styles.new_account_wrapper, 'text-muted')}>
                {t`Уже есть аккаунт?`}&nbsp;
                <Link to={AppUrls.LOGIN} className={styles.register_link}>{t`Войти`}</Link>
            </p>
        </AuthWrapper>
    );
};

export default Registration;